<template lang="pug">
web-app-layout(:showLogo="true" :noPadding="true")
  .purchased-stickers-container
    web-page-header(title="Purchase History" subtitle="View and manage all your purchased stickers in one place")

    //- Search Section
    .search-section
      .search-wrapper
        .search-input-container
          img(src="@/assets/icons/searchIcon.svg" alt="Search").search-icon
          input(
            v-model="state.searchTerm"
            placeholder="Search your purchases..."
            @input="handleSearchInput"
            type="text"
          ).search-input
          button(
            v-if="state.searchTerm"
            @click="clearSearch"
            type="button"
          ).clear-button
            img(src="@/assets/icons/close.svg" alt="Clear")

    //- Loading State
    web-page-skeleton(v-if="state.isLoading && state.stickers.length === 0" :sections="1" :cardsPerSection="12")

    //- Content
    .content-section(v-if="!state.isLoading || state.stickers.length > 0")
      stickers-grid-template(:stickers="state.filteredStickers" :show-purchase-details="true")

      //- Load More button
      .load-more-section(v-if="state.hasMoreItems && !state.isLoading")
        button(
          @click="loadMore"
          :disabled="state.isLoadingMore"
          type="button"
        ).load-more-button
          span(v-if="!state.isLoadingMore") Load More
          span(v-else) Loading...

      //- Loading more indicator
      .loading-more-section(v-if="state.isLoadingMore")
        .loading-spinner-small

    //- Error State
    .error-state(v-if="!state.isLoading && state.loadError")
      .error-icon ⚠️
      p.error-text We couldn't load your purchases
      button.retry-button(@click="searchPurchases" type="button")
        span Try Again

    //- Empty State
    .empty-state(v-if="!state.isLoading && !state.loadError && state.filteredStickers.length === 0")
      .empty-state-icon {{ isTicketsFilter ? '🎟' : '🛍️' }}
      h3.empty-state-title {{ isTicketsFilter ? 'No tickets found' : 'No purchases found' }}
      p.empty-state-text(v-if="state.searchTerm") Try adjusting your search terms
      p.empty-state-text(v-else-if="isTicketsFilter") You haven't purchased any event tickets yet
      p.empty-state-text(v-else) You haven't purchased any stickers yet
      button(v-if="state.searchTerm" @click="clearSearch" type="button").clear-search-button Clear Search
</template>

<script lang="ts">
import {
  computed, defineComponent, onMounted, reactive, watch,
} from 'vue';
import WebAppLayout from '../dashboard/WebAppLayout.vue';
import WebPageHeader from '../components/WebPageHeader.vue';
import WebPageSkeleton from '../components/WebPageSkeleton.vue';
import StickersGridTemplate from './StickersGridTemplate.vue';
import gqlRequest from '../dashboard/gql-request';
import { useActingEntity } from '@/composables/useActingEntity';

export default defineComponent({
  name: 'WebAppPurchasedStickers',
  components: { WebAppLayout, WebPageHeader, WebPageSkeleton, StickersGridTemplate },
  setup() {
    const {
      route, router, loading, getPurchasedStickersHistory,
    } = gqlRequest();

    const state = reactive({
      stickers: [] as any[],
      filteredStickers: [] as any[],
      searchTerm: '',
      currentPage: 1,
      itemsPerPage: 12,
      hasMoreItems: true,
      isLoading: false,
      isLoadingMore: false,
      loadError: false,
    });
    const { entityType, entityId } = useActingEntity();

    // Debounce timer
    let searchDebounceTimer: number | null = null;

    // Check if this is a tickets-only filter view (currently always false for purchase history)
    const isTicketsFilter = computed(() => false);

    function handleSearchInput() {
      // Clear existing timer
      if (searchDebounceTimer) {
        clearTimeout(searchDebounceTimer);
      }

      // Set new timer for 500ms debounce
      searchDebounceTimer = setTimeout(() => {
        searchPurchases();
      }, 500);
    }

    async function getStickers(page = 1, searchString = '', append = false) {
      if (page === 1) {
        state.isLoading = true;
        state.loadError = false;
        loading.value = true;
      } else {
        state.isLoadingMore = true;
      }

      try {
        // Purchases of whoever the user is acting as (themselves or an organization)
        const userId = entityId.value;

        if (!userId) {
          state.stickers = [];
          state.filteredStickers = [];
          state.hasMoreItems = false;
          return;
        }

        const skip = (page - 1) * state.itemsPerPage;
        const result = await getPurchasedStickersHistory(
          entityType.value,
          userId,
          skip,
          state.itemsPerPage,
          searchString,
        );

        // The API returns a direct array, not a paginated object
        const items = result || [];

        // Transform the data to match the expected format
        const transformedStickers = items.map((item: any) => ({
          ...item.sticker,
          purchasePrice: item.purchasePrice,
          purchaseDate: item.purchaseDate,
          purchaseId: item.id,
        }));

        if (append) {
          // Append to existing stickers
          state.stickers = [...state.stickers, ...transformedStickers];
          state.filteredStickers = [...state.filteredStickers, ...transformedStickers];
        } else {
          // Replace existing stickers
          state.stickers = transformedStickers;
          state.filteredStickers = transformedStickers;
        }

        // Check if there are more items
        state.hasMoreItems = items.length === state.itemsPerPage;
        state.currentPage = page;
      } catch (error) {
        if (!append) {
          state.stickers = [];
          state.filteredStickers = [];
          state.loadError = true;
        }
        state.hasMoreItems = false;
      } finally {
        state.isLoading = false;
        state.isLoadingMore = false;
        loading.value = false;
      }
    }

    function searchPurchases() {
      // Reset to first page when searching
      state.currentPage = 1;
      state.hasMoreItems = true;
      getStickers(1, state.searchTerm, false);
    }

    function loadMore() {
      const nextPage = state.currentPage + 1;
      getStickers(nextPage, state.searchTerm, true);
    }

    function clearSearch() {
      state.searchTerm = '';
      searchPurchases();
    }

    // Watch for route changes to reload data
    watch(() => route.path, () => {
      if (route.path.includes('/purchased') || route.path.includes('/purchase-history')) {
        state.currentPage = 1;
        state.hasMoreItems = true;
        state.searchTerm = '';
        getStickers(1, '', false);
      }
    });

    onMounted(async () => {
      // Always fetch fresh data when mounting
      await getStickers(1, '', false);
    });

    return {
      route,
      router,
      loading,
      // data
      state,
      isTicketsFilter,
      searchPurchases,
      loadMore,
      handleSearchInput,
      clearSearch,
    };
  },
});
</script>

<style scoped lang="scss">
// Main Container
.purchased-stickers-container {
  padding: 0 !important;
  min-height: 100vh;
  background: #ffffff;
  max-width: 1200px;

  :deep(.web-page-skeleton) {
    max-width: 100%;
    margin: 0;
  }
}

// Search Section
.search-section {
  padding: 24px 24px 0 24px;
  margin-bottom: 24px;
  
  @media (min-width: 768px) {
    padding: 32px 40px 0 40px;
    margin-bottom: 32px;
  }
  
  @media (min-width: 1024px) {
    padding: 40px 64px 0 64px;
  }
}

.search-wrapper {
  display: flex;
  gap: 12px;
  align-items: center;
  max-width: 100%;
  
  @media (min-width: 768px) {
    gap: 16px;
  }
}

.search-input-container {
  position: relative;
  flex: 1;
  max-width: 600px;
}

.search-icon {
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  opacity: 0.4;
  pointer-events: none;
}

.search-input {
  width: 100%;
  padding: 14px 48px 14px 48px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 16px;
  color: #1a1a1a;
  background: #ffffff;
  transition: all 0.2s;
  
  &::placeholder {
    color: #9ca3af;
  }
  
  &:focus {
    outline: none;
    border-color: #0d9488;
    box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.1);
  }
}

.clear-button {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  padding: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: background 0.2s;
  
  img {
    width: 16px;
    height: 16px;
    opacity: 0.5;
  }
  
  &:hover {
    background: #f3f4f6;
    
    img {
      opacity: 0.8;
    }
  }
}

// Loading State
.loading-container {
  padding: 80px 24px;
  text-align: center;
  
  @media (min-width: 768px) {
    padding: 100px 40px;
  }
  
  @media (min-width: 1024px) {
    padding: 120px 64px;
  }
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e5e7eb;
  border-top-color: #0d9488;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px auto;
}

.loading-spinner-small {
  width: 32px;
  height: 32px;
  border: 3px solid #e5e7eb;
  border-top-color: #0d9488;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-text {
  font-size: 16px;
  color: #6b7280;
  margin: 0;
}

// Content Section
.content-section {
  padding: 0 24px;
  margin-bottom: 40px;
  
  @media (min-width: 768px) {
    padding: 0 40px;
    margin-bottom: 48px;
  }
  
  @media (min-width: 1024px) {
    padding: 0 64px;
    margin-bottom: 56px;
  }
}

// Load More Section
.load-more-section {
  display: flex;
  justify-content: center;
  margin-top: 32px;
  padding: 0 24px;
  
  @media (min-width: 768px) {
    padding: 0 40px;
  }
  
  @media (min-width: 1024px) {
    padding: 0 64px;
  }
}

.load-more-button {
  padding: 12px 32px;
  background: linear-gradient(90deg, #0d9488 0%, #14b8a6 100%);
  color: white;
  border: none;
  border-radius: 9999px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(13, 148, 136, 0.3);
  
  &:hover:not(:disabled) {
    transform: scale(1.02);
    box-shadow: 0 6px 16px rgba(13, 148, 136, 0.4);
  }
  
  &:active {
    transform: scale(0.98);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
}

.loading-more-section {
  display: flex;
  justify-content: center;
  padding: 32px 24px;
  
  @media (min-width: 768px) {
    padding: 32px 40px;
  }
  
  @media (min-width: 1024px) {
    padding: 32px 64px;
  }
}

// Error State
.error-state {
  padding: 80px 24px;
  text-align: center;
}

.error-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.error-text {
  font-size: 16px;
  color: #dc2626;
  margin: 0 0 24px 0;
  line-height: 1.6;
  font-weight: 600;
}

.retry-button {
  padding: 12px 24px;
  background: #0d9488;
  color: white;
  border: none;
  border-radius: 9999px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #0f766e;
    transform: scale(1.02);
  }
}

// Empty State
.empty-state {
  padding: 80px 24px;
  text-align: center;
  
  @media (min-width: 768px) {
    padding: 100px 40px;
  }
  
  @media (min-width: 1024px) {
    padding: 120px 64px;
  }
}

.empty-state-icon {
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.3;
}

.empty-state-title {
  font-size: 24px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 8px 0;
}

.empty-state-text {
  font-size: 16px;
  color: #6b7280;
  margin: 0 0 24px 0;
  line-height: 1.6;
}

.clear-search-button {
  padding: 12px 24px;
  background: #0d9488;
  color: white;
  border: none;
  border-radius: 9999px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #0f766e;
    transform: scale(1.02);
  }
}

// Responsive adjustments
@media screen and (max-width: 500px) {
  .search-input-container {
    max-width: 100%;
  }
}
</style>
